import {
  getInt32,
  getList,
  LeoRPCRequest,
  LeoRPCResponse,
  LeoRPCResult,
  LeoRPC
} from "@surya-digital/leo-ts-types";
import { PublicAPIKeyDetails, PublicAPIKeyDetailsDTO } from "./publicApiKeyDetails";
/* eslint-disable import/export */
export abstract class GetPublicAPIKeysRPC implements
  LeoRPC<GetPublicAPIKeysRPC.Request, GetPublicAPIKeysRPC.Response, GetPublicAPIKeysRPC.Errors.Errors>
{
  abstract execute(
    request: GetPublicAPIKeysRPC.Request
  ): Promise<LeoRPCResult<GetPublicAPIKeysRPC.Response, GetPublicAPIKeysRPC.Errors.Errors>>;
}

export namespace GetPublicAPIKeysRPC {

  export class InvalidRequestError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidRequestError";
    }
  }

  export interface RequestDTO {
    pageIndex: number;
    itemsPerPage: number;
  }
  
  export class Request implements LeoRPCRequest {
    readonly isGetPublicAPIKeysRPCRequest: boolean = true;
  
    readonly pageIndex: number;
    readonly itemsPerPage: number;
  
    static readonly constraints = {
      "pageIndex": {
        "minValue": 0,
      },
      "itemsPerPage": {
        "minValue": 10,
      }
    }
  
    constructor(
    pageIndex: number, 
    itemsPerPage: number
    ) {
      Request.validatePageIndex(pageIndex);
      this.pageIndex = pageIndex;
      Request.validateItemsPerPage(itemsPerPage);
      this.itemsPerPage = itemsPerPage;
    }
  
    static fromDTO(dto: RequestDTO): Request {
      const pageIndex = getInt32(dto, "pageIndex", InvalidRequestPageIndexError);
      const itemsPerPage = getInt32(dto, "itemsPerPage", InvalidRequestItemsPerPageError);
      return new Request(
        pageIndex,
        itemsPerPage
      );
    }
  
    toDTO(): RequestDTO {
      const dto: RequestDTO = {
          pageIndex: this.pageIndex,
          itemsPerPage: this.itemsPerPage
      };
      return dto;
    }
    private static validatePageIndex = (pageIndex: number): void => {
      if(pageIndex < 0) {
        throw new InvalidRequestPageIndexMinValueError(
          0,
          `Attribute pageIndex has value ${pageIndex}. Min value is 0.`
        );
      }
    }
    private static validateItemsPerPage = (itemsPerPage: number): void => {
      if(itemsPerPage < 10) {
        throw new InvalidRequestItemsPerPageMinValueError(
          10,
          `Attribute itemsPerPage has value ${itemsPerPage}. Min value is 10.`
        );
      }
    }
    
  }
  
  Request.prototype.toString = function toString(): string {
    return `GetPublicAPIKeysRPC(pageIndex='${this.pageIndex}',itemsPerPage='${this.itemsPerPage}')`;
  };
  

  export class InvalidResponseError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidResponseError";
    }
  }

  export interface ResponseDTO {
    apiKeys: PublicAPIKeyDetailsDTO[];
    totalItems: number;
  }
  
  export class Response implements LeoRPCResponse {
    readonly isGetPublicAPIKeysResponse: boolean = true;
  
    readonly apiKeys: PublicAPIKeyDetails[];
    readonly totalItems: number;
  
    static readonly constraints = {
      "totalItems": {
        "minValue": 0,
      }
    }
  
    constructor(
    apiKeys: PublicAPIKeyDetails[], 
    totalItems: number
    ) {
      this.apiKeys = apiKeys;
      Response.validateTotalItems(totalItems);
      this.totalItems = totalItems;
    }
  
    static fromDTO(dto: ResponseDTO): Response {
      const apiKeys = getList<PublicAPIKeyDetailsDTO, PublicAPIKeyDetails>(
        dto,
        "apiKeys",
        PublicAPIKeyDetails.fromDTO,
        InvalidResponseApiKeysError
      );
      const totalItems = getInt32(dto, "totalItems", InvalidResponseTotalItemsError);
      return new Response(
        apiKeys,
        totalItems
      );
    }
  
    toDTO(): ResponseDTO {
      const dto: ResponseDTO = {
          apiKeys: this.apiKeys.map((e: PublicAPIKeyDetails) => { return e.toDTO() }),
          totalItems: this.totalItems
      };
      return dto;
    }
    private static validateTotalItems = (totalItems: number): void => {
      if(totalItems < 0) {
        throw new InvalidResponseTotalItemsMinValueError(
          0,
          `Attribute totalItems has value ${totalItems}. Min value is 0.`
        );
      }
    }
    
  }
  
  Response.prototype.toString = function toString(): string {
    return `GetPublicAPIKeys(apiKeys='${this.apiKeys}',totalItems='${this.totalItems}')`;
  };
  


  export namespace Errors {

    export type Errors = any;
    export type ErrorsDTO = any;

    export const fromDTO = (dto: ErrorsDTO): Errors => {
      switch (dto.code) {
        default:
          throw new InvalidResponseError(`Unexpected error code: ${dto.code}`);
      }
    };
  }
}

export class InvalidRequestPageIndexError extends GetPublicAPIKeysRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestPageIndexError";
  }
}

export class InvalidRequestPageIndexMinValueError extends InvalidRequestPageIndexError {
  readonly minValue: number;
  constructor(minValue: number, message?: string) {
    super(message);
    this.minValue = minValue;
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestPageIndexMinValueError";
  }
}

export class InvalidRequestItemsPerPageError extends GetPublicAPIKeysRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestItemsPerPageError";
  }
}

export class InvalidRequestItemsPerPageMinValueError extends InvalidRequestItemsPerPageError {
  readonly minValue: number;
  constructor(minValue: number, message?: string) {
    super(message);
    this.minValue = minValue;
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestItemsPerPageMinValueError";
  }
}

export class InvalidResponseApiKeysError extends GetPublicAPIKeysRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseApiKeysError";
  }
}

export class InvalidResponseTotalItemsError extends GetPublicAPIKeysRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseTotalItemsError";
  }
}

export class InvalidResponseTotalItemsMinValueError extends InvalidResponseTotalItemsError {
  readonly minValue: number;
  constructor(minValue: number, message?: string) {
    super(message);
    this.minValue = minValue;
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseTotalItemsMinValueError";
  }
}

